import Link from 'next/link';
import { useRouter } from 'next/router';
import ShopSelectorIcon from 'public/images/common/shopSelector.svg';
import { useContext, useEffect, useRef, useState } from 'react';
import { NextImage } from 'src/components/shared/elements/NextImage';
import { KaimonoWebRoutes } from 'src/config/routes';
import { LIMITED_SALE_FEATURE_TAG_ID } from 'src/constants/featureTags';
import { useModal } from 'src/hooks/useModal';
import { useProducts } from 'src/hooks/useProducts';
import { useShopSelector } from 'src/hooks/useShopSelector';
import { Type as UseProductsFeatureTagType } from 'src/libs/api/productsApi';
import { I18nContext } from 'src/stores/I18n';
import { mixpanel } from 'src/utils/mixpanel';
import { SubNavi } from '../SubNavi';
import styles from './index.module.scss';

export const Navi: React.FC = () => {
  const router = useRouter();
  const localeResources = useContext(I18nContext);
  const { setIsVisible, setIgnitionSource } = useModal('shopSelectModal');
  const startList = useRef<HTMLDivElement>(null);
  const endList = useRef<HTMLDivElement>(null);
  const [isSubNaviOpen, setIsSubNaviOpen] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const { currentShop } = useShopSelector();
  const { products: limitedSaleProducts } = useProducts(
    LIMITED_SALE_FEATURE_TAG_ID,
    UseProductsFeatureTagType.FEATURE_TAG_ID,
    1,
    1
  );

  useEffect(() => {
    setMounted(true);
    // 別ページ遷移時にナビを閉じる
    const handleRouteChange = () => setIsSubNaviOpen(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  return mounted ? (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div ref={startList} />
          <div
            className={styles.item}
            onClick={() => {
              mixpanel.track('TopNaviMenuClicked', { type: 'category' });
              setIsSubNaviOpen((isOpen) => !isOpen);
            }}
          >
            {localeResources.category.title}
          </div>
          <Link href={KaimonoWebRoutes.brands.list}>
            <a
              className={styles.item}
              onClick={() => {
                mixpanel.track('TopNaviMenuClicked', {
                  type: 'brandsList'
                });
              }}
            >
              {localeResources.brands.titleName}
            </a>
          </Link>
          <Link href={KaimonoWebRoutes.couponList}>
            <a
              className={styles.item}
              onClick={() =>
                mixpanel.track('TopNaviMenuClicked', {
                  type: 'coupon-list'
                })
              }
            >
              {localeResources.allVisitorCampaign.getCoupon}
            </a>
          </Link>
          {!!limitedSaleProducts?.length && (
            <Link href={KaimonoWebRoutes.featured.limited_sale}>
              <a
                className={styles.item}
                onClick={() =>
                  mixpanel.track('TopNaviMenuClicked', {
                    type: 'limited-time-sale'
                  })
                }
              >
                {localeResources.featured.limitedSale.title}
              </a>
            </Link>
          )}
          <Link href={KaimonoWebRoutes.ranking.index}>
            <a
              className={styles.item}
              onClick={() => {
                mixpanel.track('TopNaviMenuClicked', {
                  type: 'ranking'
                });
              }}
            >
              {localeResources.ranking.titleName}
            </a>
          </Link>
          <Link href={KaimonoWebRoutes.localSouvenirs.index}>
            <a
              className={styles.item}
              onClick={() => {
                mixpanel.track('TopNaviMenuClicked', {
                  type: 'local-souvenirs'
                });
              }}
            >
              {localeResources.localSouvenirs.japan.navi}
            </a>
          </Link>
          <Link href={KaimonoWebRoutes.recommended}>
            <a
              className={styles.item}
              onClick={() => {
                mixpanel.track('TopNaviMenuClicked', {
                  type: 'must-buy'
                });
              }}
            >
              {localeResources.recommended.mustBuyPopularJapaneseGoods}
            </a>
          </Link>
          <Link href={KaimonoWebRoutes.featured.index}>
            <a
              className={styles.item}
              onClick={() => {
                mixpanel.track('TopNaviMenuClicked', {
                  type: 'feature'
                });
              }}
            >
              {localeResources.featured.featuredCollections}
            </a>
          </Link>
          <div ref={endList} />
        </div>
        <div
          className={styles.shopSelector}
          onClick={() => {
            setIgnitionSource('global_nav');
            setIsVisible(true);
            mixpanel.onShopModalOpen('header');
          }}
        >
          <NextImage src={ShopSelectorIcon} width={22} height={17} alt={localeResources.shops.placeholder} />
          <span className={styles.text}>
            {currentShop ? currentShop.name : localeResources.airports.selectPickupAirport}
          </span>
        </div>
      </div>
      <SubNavi isSubNaviOpen={isSubNaviOpen} setIsSubNaviOpen={setIsSubNaviOpen} />
    </div>
  ) : null;
};
